import { authResource, AuthResource } from '@witkac/kartamiejska-resources';
import { Profile, Login, ProfileType } from '@witkac/kartamiejska-shared';
import jwtDecode from 'jwt-decode';

import { AbstractModule } from './abstract.module';
import { LocalStorage } from '@/enums/storage.enum';
import { clearIdbStore } from '@/utils/idbKeyval';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';
/**
 * Moduł autentykacji
 *
 * @class AuthModule
 * @extends {AbstractModule<AuthResource>}
 */
class AuthModule extends AbstractModule<AuthResource> {
  public state = {
    profile: undefined,
    permissions: {},
    refreshId: false
  };

  public mutations = {
    /**
     * Zapisuje dane profilu do store
     *
     * @param localState state
     * @param profile profil użytkownika
     */
    save(localState: any, profile: Profile): void {
      localState.profile = profile;
    },
    /**
     * Zapisuje uprawnienia do  store
     *
     * @param {*} localState
     * @param {object} permission
     */
    savePermissions(localState: any, permissions: any) {
      localState.permissions = { ...localState.permissions, ...permissions };
    },

    /**
     * Ustawia id timeoutu odświeżającego token
     *
     * @param {*} localState
     * @param {boolean} refreshId
     */
    setRefreshId(localState: any, refreshId: boolean): void {
      localState.refreshId = refreshId;
    }
  };

  public actions = {
    /**
     * Logowanie
     *
     * @param context context
     * @param data dane logowania
     */
    login: async (context: any, data: Login): Promise<{ profile: Profile; hasAllConsents: boolean }> => {
      const { token, profile, hasAllConsents } = await this.resource.login(data.login, data.password);
      if (profile.type !== ProfileType.CLIENT) {
        throw new Error('Należy użyć konta klienta lub skorzystać z panelu.');
      }

      localStorage.setItem(LocalStorage.TOKEN, token);
      localStorage.setItem(LocalStorage.PROFILE_ID, '' + profile.id);
      localStorage.setItem(LocalStorage.USER_ID, '' + profile.userId);
      context.commit('save', profile);
      context.dispatch('updateTokenOnExpire', token);
      return { profile, hasAllConsents };
    },

    /**
     * Odświeża token
     *
     * @type {async}
     * @memberof AuthModule
     */
    refreshToken: (context: any): Promise<void> => {
      return this.resource
        .refreshToken()
        .then(token => {
          localStorage.setItem(LocalStorage.TOKEN, token);
          context.commit('setRefreshId', null);
          context.dispatch('updateTokenOnExpire', token);
        })
        .catch(() => context.commit('setRefreshId', null));
    },
    /**
     * Odświeża token, gdy kończy się jego ważność
     */
    updateTokenOnExpire: (context: any, token: string): void => {
      token = token || (localStorage.getItem(LocalStorage.TOKEN) as string);
      if (context.state.refreshId || !token) {
        return;
      }
      const tokenPayload: any = jwtDecode(token);
      if (tokenPayload.exp) {
        const tokenExpInMs = tokenPayload.exp * 1000;
        const nowMs = new Date().getTime();
        const diff = tokenExpInMs - nowMs;
        // console.log("tokenExpInMs-nowMs", diff, (diff / (1000 * 60)) + ' minutes', (diff / (1000 * 60 * 60 * 24)) + ' days');
        const reservedTime = 1000 * 60 * 6;
        let timeToExpiration = diff - reservedTime;

        if (timeToExpiration < 10) {
          timeToExpiration = 1
        } else if (timeToExpiration > 36000000) {  // >10h 
          timeToExpiration = 36000000;
        }


        const timeoutId = setTimeout(
          () => context.dispatch('refreshToken')
          , timeToExpiration);
        context.commit('setRefreshId', timeoutId);
      }
    },

    /**
     * Sprawdzanie dostępności loginu
     *
     * @param {*} context
     * @param {string} login
     * @returns {Promise<boolean>}
     */
    checkLoginAvailability: (context: any, login: string): Promise<boolean> => {
      return this.resource.checkLoginAvailability(login);
    },

    /**
     * Zmiana hasła
     *
     * @param {*} context
     * @param {*} variables
     * @returns {Promise<boolean>}
     */
    changePassword: (context: any, variables: any): Promise<boolean> => this.resource.changePassword(variables),

    /**
     * Wylogowuje użytkownika
     *
     * @param {*} localState
     */
    unauthorize(localState: any) {
      if (localState.refreshId) {
        clearTimeout(localState.refreshId);
        localState.refreshId = null;
      }

      return clearIdbStore().then(() => {
        localState.profile = undefined;
        localStorage.clear();
      });
    }
  };
}

export default new AuthModule(authResource);
